import api from './client';

// Types
export interface Competitor {
  id: number;
  name: string;
  sort_order: number;
}

export interface CompetitorPrice {
  competitor_id: number;
  competitor_name: string | null;
  price: number;
  source: string;
}

export interface OilPricelist {
  id: number;
  brand: string;
  valid_from: string | null;
  valid_to: string | null;
  status: string;
  neva_discount_pct: number;
  service_cost_4l: number;
  competitor_strategy: 'min' | 'min_minus_pct';
  competitor_strategy_pct: number;
  min_margin: number;
  category_markups: Record<string, Record<string, number>>;
  categories: string[];
  items: OilPricelistItem[];
  supplier_document_id: number | null;
  document_id: number | null;
  approved_at: string | null;
  approved_by_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface OilPricelistItem {
  id: number;
  category: string;
  product_name: string;
  specs: string | null;
  volume_liters: number;
  neva_price: number | null;
  markup_pct: number | null;
  markup_override: number | null;
  sale_price_override: number | null;
  include_service: boolean;
  purchase_price: number | null;
  service_cost: number | null;
  cost_price: number | null;
  sale_price: number | null;
  price_source: 'override' | 'competitor' | 'fallback' | null;
  margin: number | null;
  margin_ok: boolean | null;
  min_competitor_price: number | null;
  competitor_prices: CompetitorPrice[];
  sort_order: number;
  catalog_id: number | null;
  size_id: number | null;
  supplier_id: number | null;
  catalog: {
    id: number;
    brand: string;
    model_name: string;
    viscosity: string | null;
    base_type: string | null;
  } | null;
}

export interface OilPricelistSummary {
  id: number;
  brand: string;
  valid_from: string | null;
  valid_to: string | null;
  status: string;
  has_supplier_document: boolean;
  has_document: boolean;
  item_count: number;
  created_at: string;
  updated_at: string;
}

// API calls
export const getPricelists = (status?: string) =>
  api.get<OilPricelistSummary[]>('/oil-pricelists', { params: status ? { status } : {} }).then(r => r.data);

export const createPricelist = (data: Partial<OilPricelist>) =>
  api.post<OilPricelist>('/oil-pricelists', data).then(r => r.data);

export const getPricelist = (id: number) =>
  api.get<OilPricelist>(`/oil-pricelists/${id}`).then(r => r.data);

export const updatePricelist = (id: number, data: Partial<OilPricelist>) =>
  api.put<OilPricelist>(`/oil-pricelists/${id}`, data).then(r => r.data);

export const deletePricelist = (id: number) =>
  api.delete(`/oil-pricelists/${id}`).then(r => r.data);

export const addItem = (pricelistId: number, data: Partial<OilPricelistItem>) =>
  api.post<OilPricelistItem>(`/oil-pricelists/${pricelistId}/items`, data).then(r => r.data);

export const updateItem = (itemId: number, data: Partial<OilPricelistItem>) =>
  api.put<OilPricelistItem>(`/oil-pricelists/items/${itemId}`, data).then(r => r.data);

export const deleteItem = (itemId: number) =>
  api.delete(`/oil-pricelists/items/${itemId}`).then(r => r.data);

export const setCompetitorPrice = (itemId: number, competitorId: number, price: number | null) =>
  api.put(`/oil-pricelists/items/${itemId}/competitors/${competitorId}`, { price }).then(r => r.data);

export const getCompetitors = () =>
  api.get<Competitor[]>('/oil-pricelists/competitors').then(r => r.data);

export const createCompetitor = (name: string) =>
  api.post<Competitor>('/oil-pricelists/competitors', { name }).then(r => r.data);

export const deleteCompetitor = (id: number) =>
  api.delete(`/oil-pricelists/competitors/${id}`).then(r => r.data);

export const updateCategoryMarkups = (pricelistId: number, category: string, markups: Record<string, number>) =>
  api.put(`/oil-pricelists/${pricelistId}/categories/${encodeURIComponent(category)}/markups`, { markups }).then(r => r.data);

export const importExcel = (pricelistId: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/oil-pricelists/${pricelistId}/import-excel`, fd).then(r => r.data);
};

export const uploadSupplierFile = (pricelistId: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<{ ok: boolean; supplier_document_id: number }>(`/oil-pricelists/${pricelistId}/supplier-file`, fd).then(r => r.data);
};

export const syncNevaPrices = (pricelistId: number) =>
  api.post<{ updated: number; neva_supplier_id: number }>(`/oil-pricelists/${pricelistId}/sync-neva-prices`).then(r => r.data);

export const activatePricelist = (pricelistId: number) =>
  api.post<{
    status: string;
    retail_prices_created: number;
    retail_prices_updated: number;
    other_pricelists_archived: number;
    document_id: number;
  }>(`/oil-pricelists/${pricelistId}/activate`).then(r => r.data);

export const openPrintForm = async (pricelistId: number) => {
  const r = await api.get(`/oil-pricelists/${pricelistId}/print`, { responseType: 'blob' });
  const blob = new Blob([r.data], { type: 'text/html' });
  window.open(URL.createObjectURL(blob), '_blank');
};

export const getRetailPrices = (params?: { catalog_id?: number; size_id?: number; brand?: string }) =>
  api.get<{
    id: number;
    catalog_id: number;
    size_id: number | null;
    sale_price: number;
    service_included: boolean;
    source: string;
    valid_from: string | null;
  }[]>('/oil-pricelists/retail-prices/by-catalog', { params }).then(r => r.data);
